import styled, { css, keyframes } from 'styled-components';

import { Input, PerfectScrollbar } from '@components';
import { TransientProps } from '@common/types';

type ContainerProps = TransientProps<{ disabled: boolean; hide: boolean }>;

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.8); }
  to { opacity: 1; transform: scale(1); }
`;

export const Container = styled.form<ContainerProps>`
  ${({ $disabled: isDisabled, $hide: shouldHide }) => css`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    transition: 180ms ease-in-out;

    ${isDisabled &&
    css`
      opacity: 0;
      pointer-events: none;
      user-select: none;
    `}

    ${shouldHide &&
    css`
      display: none;
    `}
  `}
`;

export const WordCloudWrapper = styled(PerfectScrollbar)`
  ${({ theme }) => css`
    width: 100%;
    overflow: hidden;
    max-height: 100%;

    h3 {
      display: flex;
      gap: 0.5rem;
      padding: 1rem;
      font-size: 1rem;
      font-weight: 500;
      padding-bottom: 0.5rem;
      border-bottom: 1px solid ${theme.colors.background[80]};

      > div {
        overflow: hidden;
      }
    }
  `}
`;

export const CloudContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.375rem 0.625rem;
  padding: 1rem;
`;

type WordSize = 'xl' | 'lg' | 'md' | 'sm' | 'xs';

type WordProps = TransientProps<{
  size: WordSize;
  index: number;
}>;

const wordSizes: Record<
  WordSize,
  { fontSize: string; fontWeight: number; opacity: number }
> = {
  xl: { fontSize: '2.25rem', fontWeight: 800, opacity: 1 },
  lg: { fontSize: '1.625rem', fontWeight: 700, opacity: 0.85 },
  md: { fontSize: '1.125rem', fontWeight: 600, opacity: 0.65 },
  sm: { fontSize: '0.875rem', fontWeight: 500, opacity: 0.45 },
  xs: { fontSize: '0.6875rem', fontWeight: 500, opacity: 0.3 },
};

export const Word = styled.span<WordProps>`
  ${({ theme, $size: size, $index: index }) => {
    const config = wordSizes[size];
    const isPrimary = index % 2 === 0;
    const color = isPrimary
      ? theme.colors.brand.primary.default
      : theme.colors.brand.secondary.default;

    return css`
      font-size: ${config.fontSize};
      font-weight: ${config.fontWeight};
      opacity: ${config.opacity};
      color: ${color};
      cursor: pointer;
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      line-height: 1.2;
      animation: ${fadeIn} 0.3s ease-out backwards;
      animation-delay: ${index * 30}ms;

      &:hover {
        transform: scale(1.1);
        opacity: 1;
        text-shadow: 0 0 20px color-mix(in srgb, ${color} 30%, transparent);
      }
    `;
  }}
`;

export const WordCloudInput = styled(Input)`
  height: 3.25rem;
  max-width: calc(100% - 2rem);
  margin: 0 1rem 1rem;

  > div svg {
    margin: 0 0 0 0.5rem;
  }

  input {
    font-size: 0.875rem;
  }
`;
